/**
 * Auth Context
 *
 * React context for authentication state management.
 * Provides user state and auth methods to the entire application.
 *
 * Usage:
 *   import { AuthProvider, useAuth } from './contexts/AuthContext';
 *
 *   // In App.js
 *   <AuthProvider>
 *     <App />
 *   </AuthProvider>
 *
 *   // In components
 *   const { user, signIn, signOut } = useAuth();
 */

import React, { useState, useEffect, useRef, createContext, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import authService from '../services/authService';

// Create context
const AuthContext = createContext(null);

/**
 * Hook to access auth context
 * @returns {object} Auth context value
 */
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}

/**
 * Auth Provider Component
 * Manages authentication state and provides auth methods
 */
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // Error state now supports { message, severity } for colored toasts
  const [error, setError] = useState({ message: '', severity: 'error' });
  const navigate = useNavigate();

  // Ref to track current user ID for auth state deduplication
  const currentUserIdRef = useRef(null);

  /**
   * Get navigation destination based on user role
   */
  const getDestination = useCallback((role) => {
    return (role === 'admin' || role === 'manager') ? '/manager' : '/tech';
  }, []);

  /**
   * Navigate user if on login page
   */
  const navigateIfOnLogin = useCallback((profile) => {
    const currentPath = window.location.pathname;
    if (currentPath === '/login' || currentPath === '/') {
      const destination = getDestination(profile.role);
      console.log('[AuthContext] Navigating to:', destination);
      navigate(destination);
    }
  }, [navigate, getDestination]);

  /**
   * Handle inactive user - sign out and show error
   */
  const handleInactiveUser = useCallback(async (profile) => {
    console.log('[AuthContext] User account is deactivated:', profile?.full_name);
    await authService.signOut();
    setUser(null);
    setError({ message: 'Your account has been deactivated. Please contact your manager.', severity: 'error' });
    navigate('/login');
  }, [navigate]);

  /**
   * Process auth session and set user state
   */
  const processSession = useCallback(async (session, skipIfSameUser = true) => {
    if (!session?.user) {
      setLoading(false);
      return;
    }

    // DEDUPLICATION: Skip if we already have this user
    if (skipIfSameUser && currentUserIdRef.current === session.user.id) {
      console.log('[AuthContext] Skipping redundant profile fetch - already have user:', session.user.id);
      setLoading(false);
      return;
    }

    try {
      const { profile, source } = await authService.resolveProfile(session);

      if (!profile) {
        // Handle different profile resolution failure cases
        if (source === 'inactive') {
          await handleInactiveUser({ id: session.user.id });
        } else if (source === 'profile_unverified') {
          // Profile couldn't be verified - sign out for security
          console.warn('[AuthContext] Profile could not be verified - signing out');
          await authService.signOut();
          setUser(null);
          setError({ message: 'Unable to verify your profile. Please sign in again.', severity: 'error' });
          navigate('/login');
        }
        setLoading(false);
        return;
      }

      console.log(`[AuthContext] Profile resolved via ${source}:`, profile.full_name, profile.role);

      // Update state
      currentUserIdRef.current = profile.id;
      setUser(profile);
      setLoading(false);

      // Navigate if needed
      navigateIfOnLogin(profile);
    } catch (err) {
      console.error('[AuthContext] Error processing session:', err);

      // Try cache as last resort
      const cachedProfile = authService.getCachedProfile(session.user.id);
      if (cachedProfile) {
        currentUserIdRef.current = session.user.id;
        setUser({ ...cachedProfile, id: session.user.id, status: 'active' });
      }

      setLoading(false);
    }
  }, [handleInactiveUser, navigateIfOnLogin]);

  /**
   * Check initial auth state
   */
  const checkAuthState = useCallback(async () => {
    console.log('[AuthContext] Starting checkAuthState...');
    try {
      const { session, error: sessionError } = await authService.getSession();

      if (sessionError) {
        console.error('[AuthContext] Session error:', sessionError);
        setLoading(false);
        return;
      }

      if (!session) {
        console.log('[AuthContext] No session found - ready for login');
        setLoading(false);
        return;
      }

      await processSession(session, false); // Don't skip on initial check
    } catch (error) {
      console.error('[AuthContext] Error checking auth state:', error);
      setLoading(false);
    }
  }, [processSession]);

  /**
   * Set up auth state listener and session refresh
   */
  useEffect(() => {
    // Initial auth check
    checkAuthState();

    // Set up auth state listener
    // CRITICAL: Never await database calls inside this callback!
    // supabase-js has an internal lock that causes deadlock if you do.
    // See: https://github.com/supabase/gotrue-js/issues/762
    const subscription = authService.onAuthStateChange((event, session) => {
      console.log('[AuthContext] Auth event:', event, session ? 'Session active' : 'No session');

      if (event === 'SIGNED_IN') {
        // Use setTimeout to break out of callback context and avoid deadlock
        // The lock is released before we make any database calls
        console.log('[AuthContext] SIGNED_IN - scheduling profile fetch outside callback');
        setTimeout(() => processSession(session), 0);
      } else if (event === 'TOKEN_REFRESHED') {
        // Token refreshed - session is still valid, no need to re-fetch profile
        // Just ensure loading is false if we were waiting
        console.log('[AuthContext] TOKEN_REFRESHED - session still valid');
        setLoading(false);
      } else if (event === 'SIGNED_OUT') {
        currentUserIdRef.current = null;
        setUser(null);
        setLoading(false);
        navigate('/login');
      }
    });

    // Safety timeout to prevent infinite loading
    const safetyTimeout = setTimeout(() => {
      setLoading(prevLoading => {
        if (prevLoading) {
          console.warn('[AuthContext] Loading timeout after 30s - forcing completion');
          return false;
        }
        return prevLoading;
      });
    }, 30000);

    // Session refresh interval (every 6 hours for 7-day sessions)
    const refreshInterval = setInterval(async () => {
      console.log('[AuthContext] Checking session refresh...');
      await authService.refreshSessionIfNeeded();
    }, 6 * 60 * 60 * 1000);

    return () => {
      subscription.unsubscribe();
      clearInterval(refreshInterval);
      clearTimeout(safetyTimeout);
    };
  }, [checkAuthState, processSession, navigate]);

  /**
   * Sign in with username/password
   */
  const signIn = useCallback(async (username, password) => {
    const result = await authService.signInWithPassword(username, password);

    if (!result.success) {
      return { success: false, error: result.error };
    }

    if (result.profile) {
      setUser(result.profile);
      navigate(getDestination(result.profile.role));
    }

    return { success: true };
  }, [navigate, getDestination]);

  /**
   * Sign in with phone (OTP)
   */
  const signInWithPhone = useCallback(async (phone, action, code = null) => {
    try {
      if (action === 'send') {
        return await authService.sendOtp(phone);
      }

      if (action === 'verify') {
        const result = await authService.verifyOtp(phone, code);

        if (result.success) {
          // The auth state listener will handle profile and navigation
          console.log('[AuthContext] Verification successful, auth state listener will handle navigation');
          return { success: true, message: 'Verification successful!' };
        }

        return {
          success: false,
          error: result.error || 'Verification failed. Please try again.',
          attempts: result.attempts
        };
      }
    } catch (error) {
      console.error('[AuthContext] Unexpected error in signInWithPhone:', error);
      return { success: false, error: 'Authentication failed. Please try again.' };
    }
  }, []);

  /**
   * Sign out
   */
  const signOut = useCallback(async () => {
    currentUserIdRef.current = null;
    await authService.signOut();
    setUser(null);
    navigate('/login');
  }, [navigate]);

  /**
   * Show error/success/warning message with toast
   * @param {string} message - Message to display
   * @param {string} severity - Toast severity: 'error' | 'success' | 'warning' | 'info'
   */
  const showError = useCallback((message, severity = 'error') => {
    setError({ message, severity });
  }, []);

  /**
   * Clear error message
   */
  const clearError = useCallback(() => {
    setError({ message: '', severity: 'error' });
  }, []);

  // Context value
  const value = {
    user,
    loading,
    error,
    signIn,
    signInWithPhone,
    signOut,
    showError,
    clearError
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export default AuthContext;
